
import { isFinite } from 'lodash';
import { Options, Vue } from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import { Point, Size } from '@/uikit/bbox/types';
import { drawPolygon, drawPolyLine } from '@/uikit/draw/draw-utils';
import { Colors, PointAsArray, PolyLine } from '@/uikit/draw/types';
import { getLayoutSizeStyle } from '@/uikit/image-viewer/image-helpers';
import { LineTrackProp } from '@/uikit/image-viewer/types';

function scalePoint(point: PointAsArray, scale: number) {
  if (isFinite(scale)) {
    return [point[0] * scale, point[1] * scale];
  }
}

@Options({
  name: 'NImageViewerROILayer',
  components: {},
  emits: []
})
export default class NImageViewerROILayer extends Vue {
  @Prop({ type: Object, required: true })
  readonly size!: Size;

  @Prop({ type: Object, default: () => ({ x: 0, y: 0 }) })
  readonly offset!: Point;

  @Prop({ type: Number, default: 1 })
  readonly scale!: number;

  @Prop({ type: Array, default: () => [] })
  readonly points?: LineTrackProp;

  @Ref('canvasContainer')
  canvasContainer: HTMLDivElement | null = null;

  canvas: HTMLCanvasElement | null = null;
  canvasContext: CanvasRenderingContext2D | null = null;
  computedPoints: PolyLine | null = null;

  get scaledLine() {
    return this.computedPoints?.map((v) => scalePoint(v, this.scale)) as PolyLine;
  }

  get style() {
    return getLayoutSizeStyle(this.size, this.offset, this.scale);
  }

  @Watch('points', { immediate: true })
  async computePoints() {
    if (this.points) {
      this.computedPoints = typeof this.points === 'function' ? await this.points() : this.points;
      this.draw();
    }
  }

  @Watch('scale')
  @Watch('size', { deep: true, immediate: true })
  resizeCanvas() {
    if (this.canvas) {
      this.canvas.width = this.size.width * this.scale;
      this.canvas.height = this.size.height * this.scale;
    } else {
      console.warn("[ROI Image Viewer]: Can't set canvas size. Canvas property undefined!");
    }
    this.draw();
  }

  createCanvas() {
    this.canvas = document.createElement('canvas');
    this.canvasContext = this.canvas.getContext('2d');
    this.canvasContainer?.appendChild(this.canvas);
    this.resizeCanvas();
  }

  clearCanvas() {
    this.canvasContext && this.canvasContext.clearRect(0, 0, this.canvas!.width, this.canvas!.height);
  }

  draw() {
    this.clearCanvas();
    this.drawLine();
  }

  drawLine() {
    if (!this.canvasContext || !this.scaledLine?.length) return;
    const ctx = this.canvasContext;
    drawPolygon(ctx, this.scaledLine, { strokeStyle: Colors.ROI, lineWidth: 4, lineCap: 'round' });
  }

  mounted() {
    this.createCanvas();
    this.draw();
  }
}
